import Link from 'next/link';
import Image from 'next/image';

interface BlogCardProps {
  post: {
    slug: string;
    title: string;
    excerpt: string;
    date: string;
    author: string;
    category: string;
    readTime: string;
    image: string;
  };
  priority?: boolean;
}

/**
 * Text-led cover used when a post has no artwork (e.g. changelog entries),
 * so the grid stays visually consistent without forcing a stock image.
 */
function FallbackCover({ category }: { category: string }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/25 via-surface-container-high to-primary/5">
      <div className="flex flex-col items-center gap-2 text-center px-6">
        <span className="font-display font-extrabold text-3xl text-primary/70 tracking-tight">Snowpad</span>
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground">
          {category}
        </span>
      </div>
    </div>
  );
}

export default function BlogCard({ post, priority = false }: BlogCardProps) {
  return (
    <Link
      href={`/blog/${post.slug}`}
      className="group rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
    >
      <article className="frost-panel ghost-border rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300 flex flex-col h-full bg-surface-container/30">
        <div className="relative h-48 w-full overflow-hidden bg-surface-container-high">
          {post.image ? (
            <Image
              src={post.image}
              alt={post.title}
              fill
              loading={priority ? 'eager' : 'lazy'}
              priority={priority}
              fetchPriority={priority ? 'high' : 'auto'}
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              quality={75}
              placeholder="empty"
              className="object-cover transition-transform duration-500 will-change-transform group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
            />
          ) : (
            <FallbackCover category={post.category} />
          )}
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-primary/90 text-on-primary text-[10px] font-bold uppercase tracking-widest backdrop-blur-md">
            {post.category}
          </span>
        </div>

        <div className="p-6 flex flex-col flex-1">
          <div className="flex items-center gap-3 mb-4 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
            <time dateTime={post.date}>{post.date}</time>
            <span className="w-1 h-1 rounded-full bg-primary/30" />
            <span>{post.readTime}</span>
          </div>

          <h3 className="text-xl font-bold text-foreground mb-3 font-display leading-tight group-hover:text-primary transition-colors">
            {post.title}
          </h3>

          <p className="text-muted-foreground font-medium text-sm leading-relaxed line-clamp-3 mb-6 flex-1">
            {post.excerpt}
          </p>

          <div className="flex items-center gap-3 pt-6 border-t border-outline-variant/30">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
              {post.author.split(' ').map(n => n[0]).join('')}
            </div>
            <span className="text-xs font-bold text-foreground uppercase tracking-wider">{post.author}</span>
          </div>
        </div>
      </article>
    </Link>
  );
}
